import { Row, SortingFn, sortingFns } from '@tanstack/react-table';
import { MarketplacePaymentLineWithSaleAndEventInfo } from 'src/hooks/useGetMarketplacePaymentLineTableData';
import { sortGroupType } from 'src/modals/GroupListings/components/groupingUtils';
import { ListingWithEvent } from 'src/tables/ListingTable/ListingTable.types';
import { SaleWithEvent } from 'src/tables/SalesTable/SalesTable.type';
import {
  ListingGroup,
  ListingNote,
  PurchaseOrderTicketGroup,
  ReportValueType,
  Seating,
  UiMoney,
} from 'src/WebApiController';

import { compareNumber } from './miscUtils';

export const localeCompareNumeric = <T>(
  rowA: Row<T | null>,
  rowB: Row<T | null>,
  colId: string
) => {
  const valueA = rowA.getValue<string>(colId);
  const valueB = rowB.getValue<string>(colId);
  if (!valueA) return valueB ? -1 : 1;
  if (!valueB) return 1;

  return valueA.localeCompare(valueB, undefined, {
    numeric: true,
  });
};

export const sortMoney = <T>(
  rowA: Row<T | null>,
  rowB: Row<T | null>,
  colId: string
) => {
  const valueA = rowA.getValue(colId) as UiMoney;
  const valueB = rowB.getValue(colId) as UiMoney;
  if (valueA == null) return valueB ? -1 : 1;
  if (valueB == null) return 1;

  const amtA = valueA.amt;
  const amtB = valueB.amt;

  return amtA - amtB;
};

export const sortNullableBoolean = <T>(
  rowA: Row<T | null>,
  rowB: Row<T | null>,
  colId: string
) => {
  let valueA = rowA.getValue(colId) as boolean | null;
  let valueB = rowB.getValue(colId) as boolean | null;

  valueA = valueA ?? false;
  valueB = valueB ?? false;

  return valueA ? 1 : valueB ? -1 : 0;
};

export const sortMixedSingleAndArray = <T>(
  rowA: Row<T | null>,
  rowB: Row<T | null>,
  colId: string
) => {
  const valueA = rowA.getValue(colId) as string | string[] | null;
  const valueB = rowB.getValue(colId) as string | string[] | null;

  const processedValueA = Array.isArray(valueA) ? (valueA[0] ?? '') : valueA;
  const processedValueB = Array.isArray(valueB) ? (valueB[0] ?? '') : valueB;

  const sortingFn = sortingFns.alphanumeric as SortingFn<string | null>;

  return sortingFn(
    {
      ...rowA,
      getValue: () => processedValueA,
    } as Row<string | null>,
    {
      ...rowB,
      getValue: () => processedValueB,
    } as Row<string | null>,
    colId
  );
};

const getNumericPrefix = (str: string | null) => {
  if (!str) return null;

  const match = str.match(/^\d+/);
  return match ? parseInt(match[0]) : null;
};

export const sortByNumericPrefix = <T>(
  rowA: Row<T | null>,
  rowB: Row<T | null>,
  colId: string
) => {
  const valueA = rowA.getValue(colId) as string;
  const valueB = rowB.getValue(colId) as string;
  if (!valueA) return valueB ? -1 : 1;
  if (!valueB) return 1;

  return compareByNumericPrefix(valueA, valueB);
};

export const compareByNumericPrefix = (
  strA: string | null,
  strB: string | null
) => {
  const numA = getNumericPrefix(strA);
  const numB = getNumericPrefix(strB);

  const cmpNumberResult = compareNumber(numA, numB);
  if (cmpNumberResult !== 0) {
    return cmpNumberResult;
  }

  const postfixA = strA ? strA.slice(numA ? numA.toString().length : 0) : '';
  const postfixB = strB ? strB.slice(numB ? numB.toString().length : 0) : '';

  return postfixA < postfixB ? -1 : 1;
};

const compareGeneric = (a: number | string, b: number | string) => {
  if (a === b) return 0;
  if (a === '') return 1;
  if (b === '') return -1;
  if (typeof a === 'number' && typeof b !== 'number') return -1;
  if (typeof b === 'number' && typeof a !== 'number') return 1;
  return a < b ? -1 : 1;
};

export const sortGeneric = <T>(
  rowA: Row<T | null>,
  rowB: Row<T | null>,
  colId: string
) => {
  const convertToComparableValue = (value: unknown) => {
    const str = String(value ?? '')
      .trim()
      .toLowerCase();
    const strAsInt = parseInt(str);
    return !Number.isNaN(strAsInt) ? strAsInt : str;
  };

  const [a, b] = [
    convertToComparableValue(rowA.getValue(colId)),
    convertToComparableValue(rowB.getValue(colId)),
  ];

  return compareGeneric(a, b);
};

export const sortListingSeating = (
  rowA: Row<ListingWithEvent | null>,
  rowB: Row<ListingWithEvent | null>,
  colId: string
) => {
  if (rowA.original?.isLtGrp && rowB.original?.listing?.isLtGrp) {
    return sortGroupType(
      (rowA.original.listing as ListingGroup).groupType,
      (rowB.original.listing as ListingGroup).groupType
    );
  } else if (rowA.original?.isLtGrp || rowB.original?.listing?.isLtGrp) {
    return rowA.original?.isLtGrp ? 1 : -1; // listing group sort first
  }

  return sortSeatingForTable(rowA, rowB, colId);
};

export const sortListingSeatingSection = (
  rowA: Row<ListingWithEvent | null>,
  rowB: Row<ListingWithEvent | null>,
  colId: string
) => {
  if (rowA.original?.isLtGrp && rowB.original?.listing?.isLtGrp) {
    return sortGroupType(
      (rowA.original.listing as ListingGroup).groupType,
      (rowB.original.listing as ListingGroup).groupType
    );
  } else if (rowA.original?.isLtGrp || rowB.original?.listing?.isLtGrp) {
    return rowA.original?.isLtGrp ? 1 : -1; // listing group sort first
  }

  return sortSeatingSection(
    rowA.original?.listing?.seating,
    rowB.original?.listing?.seating
  );
};

export const sortPurchaseOrderTicketGroupSeating = (
  rowA: Row<PurchaseOrderTicketGroup | null>,
  rowB: Row<PurchaseOrderTicketGroup | null>,
  colId: string
) => {
  if (!rowA?.original || !rowB?.original) {
    return 0;
  }

  const rowATicketGroupToGetSeating = rowA.original.isTktGrp
    ? rowA.original
    : rowA.original.ticketGroupItems?.[0];

  const rowBTicketGroupToGetSeating = rowB.original.isTktGrp
    ? rowB.original
    : rowB.original.ticketGroupItems?.[0];

  if (rowATicketGroupToGetSeating?.seating == null) {
    return -1;
  }

  if (rowBTicketGroupToGetSeating?.seating == null) {
    return -1;
  }

  return sortSeating(
    rowATicketGroupToGetSeating.seating,
    rowBTicketGroupToGetSeating.seating
  );
};

export const sortSalesSeating = (
  rowA: Row<SaleWithEvent | null>,
  rowB: Row<SaleWithEvent | null>,
  colId: string
) => {
  if (!rowA?.original || !rowB?.original) {
    return 0;
  }

  return sortSeating(rowA.original.sale.seating, rowB.original.sale.seating);
};

export const sortSalesSeatingSection = (
  rowA: Row<SaleWithEvent | null>,
  rowB: Row<SaleWithEvent | null>,
  colId: string
) => {
  if (!rowA?.original || !rowB?.original) {
    return 0;
  }

  return sortSeatingSection(
    rowA.original.sale.seating,
    rowB.original.sale.seating
  );
};

export const sortSeatingForTable = <T>(
  rowA: Row<T | null>,
  rowB: Row<T | null>,
  colId: string
) => {
  const valueA = rowA.getValue(colId);
  const valueB = rowB.getValue(colId);
  if (!valueA) return valueB ? -1 : 1;
  if (!valueB) return 1;

  const { seating: a } = valueA as {
    seating: Seating;
  };
  const { seating: b } = valueB as {
    seating: Seating;
  };

  return sortSeating(a, b);
};

export const sortSeating = (a: Seating, b: Seating) => {
  if (a.section !== b.section) {
    return compareByNumericPrefix(a.section, b.section);
  } else {
    if (a.row !== b.row) {
      return compareByNumericPrefix(a.row, b.row);
    }

    return sortSeatingSeats(a, b);
  }
};

export const sortSeatingSeatsForTable = <T>(
  rowA: Row<T | null>,
  rowB: Row<T | null>,
  colId: string
) => {
  const valueA = rowA.getValue(colId) as Seating;
  const valueB = rowB.getValue(colId) as Seating;
  if (!valueA) return valueB ? -1 : 1;
  if (!valueB) return 1;

  return sortSeatingSeats(valueA, valueB);
};

export const sortSeatingSeats = (a: Seating, b: Seating) => {
  if (a.seatFr === b.seatFr) {
    if (a.seatTo === b.seatTo) {
      return 0;
    }
    const seatToA = a.seatTo ?? '';
    const seatToB = b.seatTo ?? '';
    const seatToAInt = parseInt(seatToA);
    const seatToBInt = parseInt(seatToB);
    if (!isNaN(seatToAInt) && !isNaN(seatToBInt)) {
      return seatToAInt < seatToBInt ? -1 : 1;
    }
    return seatToA < seatToB ? -1 : 1;
  }
  const seatFromA = a.seatFr ?? '';
  const seatFromB = b.seatFr ?? '';
  const seatFromAInt = parseInt(seatFromA);
  const seatFromBInt = parseInt(seatFromB);
  if (!isNaN(seatFromAInt) && !isNaN(seatFromBInt)) {
    return seatFromAInt < seatFromBInt ? -1 : 1;
  }
  return seatFromA < seatFromB ? -1 : 1;
};

export const sortPurchaseOrderSeatingSection = (
  rowA: Row<PurchaseOrderTicketGroup | null>,
  rowB: Row<PurchaseOrderTicketGroup | null>,
  colId: string
) => {
  if (!rowA?.original || !rowB?.original) {
    return 0;
  }

  const rowATicketGroupToGetSeating = rowA.original.isTktGrp
    ? rowA.original
    : rowA.original.ticketGroupItems?.[0];

  const rowBTicketGroupToGetSeating = rowB.original.isTktGrp
    ? rowB.original
    : rowB.original.ticketGroupItems?.[0];

  if (rowATicketGroupToGetSeating?.seating == null) {
    return -1;
  }

  if (rowBTicketGroupToGetSeating?.seating == null) {
    return -1;
  }

  return sortSeatingSection(
    rowATicketGroupToGetSeating.seating,
    rowBTicketGroupToGetSeating.seating
  );
};

export const sortSeatingSection = <T>(
  seatingA: Seating | undefined,
  seatingB: Seating | undefined
) => {
  if (!seatingA) return -1;
  if (!seatingB) return 1;

  return compareByNumericPrefix(seatingA.section, seatingB.section);
};

export const safeSortDateTime = <T>(
  rowA: Row<T | null>,
  rowB: Row<T | null>,
  colId: string
) => {
  // Convert both null and empty string to 0 timestamp
  const valueA = rowA.getValue<Date | string | number>(colId) || 0;
  const valueB = rowB.getValue<Date | string | number>(colId) || 0;

  const timestampA = new Date(valueA).getTime();
  const timestampB = new Date(valueB).getTime();
  if (!isNaN(timestampA) && !isNaN(timestampB)) {
    return timestampA - timestampB;
  }
  return isNaN(timestampA) ? 1 : -1;
};

export enum GridActionType {
  Edit,
  Delete,
  Select,
  Share,
  Duplicate,
  History,
  ViewRealTime,
}

export const getNextEditableListingRow = (
  rows: ListingWithEvent[],
  currentRow: ListingWithEvent
): ListingWithEvent | undefined => {
  const currentIndex = rows.findIndex(
    (row) => row.listing?.id === currentRow.listing?.id
  );
  for (let index = currentIndex + 1; index < rows.length; index++) {
    const row = rows[index];
    if (!row.isCompListing && !row.isLtGrp) {
      return row;
    }
  }
  return undefined;
};

export const sortMarketplacePaymentLineSeating = (
  rowA: Row<MarketplacePaymentLineWithSaleAndEventInfo | null>,
  rowB: Row<MarketplacePaymentLineWithSaleAndEventInfo | null>,
  colId: string
) => {
  if (!rowA?.original?.seating || !rowB?.original?.seating) {
    return 0;
  }

  return sortSeating(rowA.original.seating, rowB.original.seating);
};

/**
 * Column filter custom function: https://tanstack.com/table/v8/docs/guide/column-filtering#custom-filter-functions
 * Assumes the column value contains a Seating object and compares by section
 * Use together with uniqueValueVariant='seatingSection' in ColumnMeta in Table.tsx
 */
export const filterSeatingSectionFn = (
  row: Row<unknown>,
  columnId: string,
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  filterValue: any
): boolean => {
  if (!filterValue) {
    return true;
  }

  const filterSectionValues = filterValue as string[];
  if (filterSectionValues.length === 0) {
    return true;
  }

  // Skip if row has children
  if (row.getLeafRows().length > 0) {
    const leafRows = row.getLeafRows();
    for (const leafRow of leafRows) {
      if (filterSeatingSectionFn(leafRow, columnId, filterValue)) {
        return true;
      }
    }

    return false;
  }

  const value = row.getValue(columnId);

  if (!value) {
    return false;
  }

  const { seating } = value as {
    seating: Seating;
  };

  return filterSectionValues.includes(seating.section);
};

/**
 * Column filter custom function: https://tanstack.com/table/v8/docs/guide/column-filtering#custom-filter-functions
 * Assumes the column value returns a list of ListingNotes and compares by listingNoteId
 * Use together with uniqueValueVariant='seatTraits' in ColumnMeta in Table.tsx
 */
export const filterSeatTraitsFn = (
  row: Row<unknown>,
  columnId: string,
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  filterValue: any
): boolean => {
  if (!filterValue) {
    return true;
  }

  const filterListingNoteIds = (filterValue as string[]).map((value) =>
    Number(value)
  );
  if (filterListingNoteIds.length === 0) {
    return true;
  }

  // Skip if row has children
  if (row.getLeafRows().length > 0) {
    const leafRows = row.getLeafRows();
    for (const leafRow of leafRows) {
      if (filterSeatTraitsFn(leafRow, columnId, filterValue)) {
        return true;
      }
    }

    return false;
  }

  const value = row.getValue(columnId) as ListingNote[] | null | undefined;

  return (
    value?.some((note) => filterListingNoteIds.includes(note.listingNoteId)) ??
    false
  );
};

/**
 * Column filter custom function: https://tanstack.com/table/v8/docs/guide/column-filtering#custom-filter-functions
 * If the filter value has any overlap with the seat range, return true
 * Use together with columnFilterVariant='seatRange' in ColumnMeta in Table.tsx
 */
export const filterSeatRangeFn = (
  row: Row<unknown>,
  columnId: string,
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  filterValue: any
): boolean => {
  if (!filterValue) {
    return true;
  }

  const [filterMin, filterMax] = filterValue as [number, number];
  if (filterMin == null && filterMax == null) {
    return true;
  }

  const seating = row.getValue<Seating>(columnId);

  if (!seating) {
    return false;
  }

  const seatFrNum = Number(seating.seatFr || 'NaN');
  const seatToNum = Number(seating.seatTo || 'NaN');

  let result = false;

  if (isNaN(seatFrNum) && isNaN(seatToNum)) {
    result = false;
  } else if (isNaN(seatToNum)) {
    if (filterMin == null) {
      result = seatFrNum <= filterMax;
    } else if (filterMax == null) {
      result = seatFrNum >= filterMin;
    } else {
      result = seatFrNum >= filterMin && seatFrNum <= filterMax;
    }
  } else {
    // seatFrNum and seatToNum are both numbers
    // return true if [seatFrNum, seatToNum] and [filterMin, filterMax] have any overlap
    if (filterMin == null) {
      result = seatFrNum <= filterMax;
    } else if (filterMax == null) {
      result = seatToNum >= filterMin;
    } else {
      result = seatFrNum <= filterMax && seatToNum >= filterMin;
    }
  }

  if (!result) {
    const leafRows = row.getLeafRows();
    for (const leafRow of leafRows) {
      if (filterSeatRangeFn(leafRow, columnId, filterValue)) {
        result = true;
        break;
      }
    }
  }

  return result;
};

/**
 * Column filter custom function: https://tanstack.com/table/v8/docs/guide/column-filtering#custom-filter-functions
 * Assumes the column value returns a string, compares case insensitive
 * Use together with uniqueValueVariant=undefined in ColumnMeta in Table.tsx
 */
export const filterStringMultiSelect = (
  row: Row<unknown>,
  columnId: string,
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  filterValue: any
): boolean => {
  if (!filterValue) {
    return true;
  }

  const filterValues = (filterValue as string[]).map((value) =>
    value.toLowerCase()
  );
  if (filterValues.length === 0) {
    return true;
  }

  // Skip if row has children
  if (row.getLeafRows().length > 0) {
    const leafRows = row.getLeafRows();
    for (const leafRow of leafRows) {
      if (filterStringMultiSelect(leafRow, columnId, filterValue)) {
        return true;
      }
    }

    return false;
  }

  const value = row.getValue(columnId) as string | null | undefined;

  if (!value) {
    return false;
  }

  return filterValues.includes(value.toLowerCase());
};

export const getReportValueTypeDisplayConfig = (
  valueType: ReportValueType | undefined
) => {
  const isNumeric =
    valueType === ReportValueType.Integer ||
    valueType === ReportValueType.Money ||
    valueType === ReportValueType.Decimal ||
    valueType === ReportValueType.IntegerList;

  const isInteger =
    valueType === ReportValueType.Integer ||
    valueType === ReportValueType.IntegerList;

  const isCurrency = valueType === ReportValueType.Money;
  const isPercent = false;
  const isDateTime =
    valueType === ReportValueType.DateTime ||
    valueType === ReportValueType.DateTimeList;

  const isBoolean =
    valueType === ReportValueType.Boolean ||
    valueType === ReportValueType.BooleanList;

  const isArray =
    valueType === ReportValueType.DateTimeList ||
    valueType === ReportValueType.IntegerList ||
    valueType === ReportValueType.GuidList ||
    valueType === ReportValueType.UserList ||
    valueType === ReportValueType.StringList ||
    valueType === ReportValueType.BooleanList;

  return {
    isNumeric,
    isInteger,
    isCurrency,
    isPercent,
    isDateTime,
    isBoolean,
    isArray,
  };
};
